/**
 * Bow library — DERIVED from the canonical catalog. Do not add entries here.
 *
 * 2026-06-10: this file used to carry its own hand-curated bow list
 * (~50 models, several with wrong specs — e.g. ARC 30 at 342 IBO vs the
 * real 348, Lift RS at 30" ATA vs the real 27.75") which drifted from the
 * canonical catalog in `src/data/bows.ts` (380+ models, manufacturer-
 * verified). BOW_LIBRARY is now generated from BOWS at module load, so
 * the My Bows picker always shows exactly the canonical catalog. To add
 * a bow, add it to the MAIN repo's `tools/arrow-builder-react/src/data/
 * bows.ts` and run `npm run lib:sync` there (scripts/check-library-sync.sh
 * gates drift at push time).
 *
 * Sal's product call (2026-06-03) stands: legacy / retired bows stay in —
 * the used market is huge and The Forge supports whatever bow the user
 * actually owns. `status` is derived from model year.
 */

import { BOWS, bowCategory, type BowModel } from '@/data/bows';

export type BowStatus = 'current' | 'recent-legacy' | 'legacy';

export interface Bow {
  brand: string;
  model: string;
  iboFps: number;
  drawWeightLb: { min: number; max: number };
  drawLengthIn: { min: number; max: number };
  /** Optional — a few compact legacy catalog entries don't carry these. */
  axleToAxleIn?: number;
  braceHeightIn?: number;
  letoffPercent?: number;
  massWeightLb?: number;
  class: 'hunting' | 'target' | 'crossover' | 'youth';
  year?: number;
  status: BowStatus;
  notes?: string;
  verificationStatus: 'verified' | 'pending';
  manufacturerUrl?: string;
}

const CURRENT_YEAR = 2026;

function toStatus(year: number): BowStatus {
  if (year >= CURRENT_YEAR - 1) return 'current';
  if (year >= CURRENT_YEAR - 4) return 'recent-legacy';
  return 'legacy';
}

/** Generic fallback ranges for compact legacy entries that don't carry them. */
const FALLBACK_DW = { min: 40, max: 70 };
const FALLBACK_DL = { min: 24, max: 31 };

function toBow(b: BowModel): Bow {
  const hasRanges = !!(b.drawWeightRange && b.drawLengthRange);
  return {
    brand: b.brand,
    model: b.model,
    iboFps: b.iboSpeed,
    drawWeightLb: b.drawWeightRange
      ? { min: b.drawWeightRange[0] ?? FALLBACK_DW.min, max: b.drawWeightRange[1] ?? FALLBACK_DW.max }
      : FALLBACK_DW,
    drawLengthIn: b.drawLengthRange
      ? { min: b.drawLengthRange[0] ?? FALLBACK_DL.min, max: b.drawLengthRange[1] ?? FALLBACK_DL.max }
      : FALLBACK_DL,
    axleToAxleIn: b.axleToAxle,
    braceHeightIn: b.brace,
    letoffPercent: b.letOff,
    massWeightLb: b.weightLb,
    class: bowCategory(b),
    year: b.year,
    status: toStatus(b.year),
    notes: hasRanges
      ? undefined
      : 'Draw weight/length ranges are generic estimates for this legacy model — verify against your bow.',
    verificationStatus: hasRanges ? 'verified' : 'pending',
  };
}

/**
 * One entry per brand+model, keeping the newest year — bow profiles store
 * brand+model only, and the picker keys options by model name, so same-name
 * year revisions (e.g. PSE Mach 30 2021/2023) collapse to the latest.
 */
const newestPerModel = new Map<string, BowModel>();
for (const b of BOWS) {
  const k = `${b.brand}|${b.model}`;
  const prev = newestPerModel.get(k);
  if (!prev || b.year > prev.year) newestPerModel.set(k, b);
}

export const BOW_LIBRARY: ReadonlyArray<Bow> = Array.from(newestPerModel.values()).map(toBow);

/** Filter bows by brand. */
export function bowsByBrand(brand: string): Bow[] {
  return BOW_LIBRARY.filter((b) => b.brand === brand);
}

/** Look up a bow by exact brand + model. */
export function getBow(brand: string, model: string): Bow | undefined {
  return BOW_LIBRARY.find((b) => b.brand === brand && b.model === model);
}

/** Filter bows by lifecycle status. */
export function bowsByStatus(status: BowStatus): Bow[] {
  return BOW_LIBRARY.filter((b) => b.status === status);
}
